import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check,
  ChevronDown,
  Globe,
  LogOut,
  Palette,
  Settings as SettingsIcon,
} from 'lucide-react';
import { Link } from 'react-router';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore, type Theme } from '../../store/themeStore';
import api from '../../services/api';
import { logger } from '../../lib/logger.js';
import type { Language } from '@trading/shared';

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'pt-BR', label: 'Português', flag: 'PT' },
  { code: 'es', label: 'Español', flag: 'ES' },
];

const THEME_OPTIONS: { key: Theme; swatch: string }[] = [
  { key: 'matrix', swatch: '#3b82f6' },
  { key: 'golden-bull', swatch: '#d4a012' },
  { key: 'cyber-purple', swatch: '#a855f7' },
  { key: 'ice', swatch: '#cbd5e1' },
];

export default function UserMenu() {
  const { t, i18n } = useTranslation();
  const { user, logout, updateUser } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'main' | 'theme' | 'lang'>('main');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setView('main');
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setView('main');
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const normalizedLang = i18n.language?.startsWith('pt')
    ? 'pt-BR'
    : i18n.language?.startsWith('es')
      ? 'es'
      : 'en';
  const currentLang =
    LANGUAGE_OPTIONS.find((l) => l.code === normalizedLang) || LANGUAGE_OPTIONS[0];
  const currentTheme = THEME_OPTIONS.find((opt) => opt.key === theme);

  const planLabel = t(
    `plans.${
      ['free', 'basic', 'pro'].includes(user?.subscription?.plan || '')
        ? user!.subscription!.plan
        : 'free'
    }`,
    { defaultValue: user?.subscription?.plan || 'free' },
  );

  const initials =
    user?.name
      ?.split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase())
      .join('') || 'U';

  const changeLanguage = async (langCode: string) => {
    i18n.changeLanguage(langCode);
    setView('main');
    if (user) {
      try {
        await api.patch('/user/preferences', { language: langCode });
        updateUser({ language: langCode as Language });
      } catch (error) {
        logger.error('Failed to save language preference:', error);
      }
    }
  };

  const selectTheme = (themeKey: Theme) => {
    setTheme(themeKey);
    setView('main');
  };

  const closeAll = () => {
    setOpen(false);
    setView('main');
  };

  return (
    <div ref={wrapperRef} data-tour="user-menu" className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setOpen((o) => !o);
          setView('main');
        }}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={user?.name || t('nav.settings')}
        className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full text-text-secondary hover:bg-bg-tertiary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary"
      >
        <span className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center text-white text-sm font-semibold shrink-0">
          {initials}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-72 rounded-xl bg-bg-elevated border border-border-default shadow-xl z-50 overflow-hidden animate-fade-in"
        >
          {view === 'main' && (
            <>
              {/* User identity */}
              <div className="px-4 py-3 flex items-center gap-3 border-b border-border-subtle">
                <span className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center text-white text-sm font-semibold shrink-0">
                  {initials}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-text-primary truncate">
                    {user?.name}
                  </p>
                  <p className="text-xs text-text-tertiary truncate">{user?.email}</p>
                  <p className="text-[11px] mt-0.5 inline-flex items-center gap-1 text-accent-primary font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-primary" />
                    {planLabel}
                  </p>
                </div>
              </div>

              {/* Quick actions */}
              <div className="py-1">
                <Link
                  to="/settings"
                  role="menuitem"
                  onClick={closeAll}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
                >
                  <SettingsIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
                  <span className="flex-1 text-left">{t('nav.settings')}</span>
                </Link>

                <button
                  type="button"
                  role="menuitem"
                  onClick={() => setView('theme')}
                  data-tour="theme-toggle"
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
                >
                  <Palette className="w-4 h-4 shrink-0" aria-hidden="true" />
                  <span className="flex-1 text-left">{t('header.selectTheme')}</span>
                  <span className="flex items-center gap-1.5 text-xs text-text-tertiary">
                    {currentTheme && (
                      <span
                        className="w-3 h-3 rounded-full border border-white/20"
                        style={{ backgroundColor: currentTheme.swatch }}
                        aria-hidden="true"
                      />
                    )}
                    {t(`header.themes.${theme}`)}
                  </span>
                </button>

                <button
                  type="button"
                  role="menuitem"
                  onClick={() => setView('lang')}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
                >
                  <Globe className="w-4 h-4 shrink-0" aria-hidden="true" />
                  <span className="flex-1 text-left">{t('header.changeLanguage')}</span>
                  <span className="text-xs text-text-tertiary uppercase">
                    {currentLang.flag}
                  </span>
                </button>
              </div>

              {/* Logout */}
              <div className="border-t border-border-subtle py-1">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    closeAll();
                    void logout();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-loss hover:bg-loss/10 transition-colors"
                >
                  <LogOut className="w-4 h-4 shrink-0" aria-hidden="true" />
                  <span className="flex-1 text-left">{t('header.logout')}</span>
                </button>
              </div>
            </>
          )}

          {view === 'theme' && (
            <SubMenu
              title={t('header.selectTheme')}
              onBack={() => setView('main')}
            >
              {THEME_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  role="menuitemradio"
                  aria-checked={theme === opt.key}
                  onClick={() => selectTheme(opt.key)}
                  className={`
                    w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors
                    ${
                      theme === opt.key
                        ? 'bg-accent-primary/10 text-accent-primary'
                        : 'text-text-secondary hover:text-text-primary hover:bg-bg-tertiary'
                    }
                  `}
                >
                  <span
                    className="w-4 h-4 rounded-full border border-white/20 shrink-0"
                    style={{ backgroundColor: opt.swatch }}
                    aria-hidden="true"
                  />
                  <span className="flex-1">{t(`header.themes.${opt.key}`)}</span>
                  {theme === opt.key && <Check className="w-4 h-4" aria-hidden="true" />}
                </button>
              ))}
            </SubMenu>
          )}

          {view === 'lang' && (
            <SubMenu
              title={t('header.changeLanguage')}
              onBack={() => setView('main')}
            >
              {LANGUAGE_OPTIONS.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  role="menuitemradio"
                  aria-checked={normalizedLang === lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`
                    w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors
                    ${
                      normalizedLang === lang.code
                        ? 'bg-accent-primary/10 text-accent-primary'
                        : 'text-text-secondary hover:text-text-primary hover:bg-bg-tertiary'
                    }
                  `}
                >
                  <span className="inline-flex items-center justify-center w-7 h-5 rounded text-[10px] font-bold bg-bg-tertiary text-text-tertiary">
                    {lang.flag}
                  </span>
                  <span className="flex-1">{lang.label}</span>
                  {normalizedLang === lang.code && (
                    <Check className="w-4 h-4" aria-hidden="true" />
                  )}
                </button>
              ))}
            </SubMenu>
          )}
        </div>
      )}
    </div>
  );
}

function SubMenu({
  title,
  onBack,
  children,
}: {
  title: string;
  onBack: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <>
      <div className="px-3 py-2 flex items-center gap-2 border-b border-border-subtle">
        <button
          type="button"
          onClick={onBack}
          className="p-1.5 rounded-md text-text-tertiary hover:text-text-primary hover:bg-bg-tertiary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary"
          aria-label={t('actions.previous')}
        >
          <ChevronDown
            className="w-4 h-4 rotate-90"
            aria-hidden="true"
          />
        </button>
        <span className="text-sm font-semibold text-text-primary">{title}</span>
      </div>
      <div className="py-1">{children}</div>
    </>
  );
}
